import type { PokerGameTileTag, TournamentState } from '@ps/poker-games-tile-mappers';
import type { select } from '@ps/web-core-store';
import type { TrackedTranslation, TranslationTag } from '@ps/web-shared-components';
import type {
  Filters,
  ColumnInstance as ReactColumnInstance,
  Column as ReactTableColumn,
  SortingRule,
  UseTableColumnOptions
} from 'react-table';
import type { DeepNonNullable, ValuesType } from 'utility-types';

import type {
  OnTournamentsUpdateSubscription,
  TournamentsQuery
} from '@swf/generated/graphql/poker/MTT/operations';
import type {
  Channel,
  Game,
  Range,
  Speed,
  Structure
} from '@swf/generated/graphql/poker/MTT/schemas';

export type MTTDataType = 'tournaments' | 'mock';

export type MTTSubcriptionDataType = 'crud' | 'crudm';

type NonNullableTournament = Omit<
  DeepNonNullable<NonNullable<ValuesType<NonNullable<TournamentsQuery['tournaments']>>>>,
  '__typename'
>;

type TournamentRawTypeName = 'Tournament' | 'Initial' | 'Create';

export type TournamentRaw = NonNullableTournament & {
  __typename: TournamentRawTypeName;
};

export interface TournamentQueryResponse {
  data?: { [K in MTTDataType]: TournamentRaw[] };
  errors?: [{ message: string }];
}

export type TornamentUpdateData = DeepNonNullable<
  NonNullable<OnTournamentsUpdateSubscription['crud']>
>;

export interface TournamentsUpdateResponse {
  data: {
    data: {
      [K in MTTSubcriptionDataType]: TornamentUpdateData;
    };
  };
}

export interface TournamentsUpdatePayload {
  tournaments: TornamentUpdateData;
  locale: string;
  timeZone: string;
}

export interface TimeHoursMinutes {
  days: number;
  hours: number;
  minutes: string;
}

export interface TournamentAttributes {
  formattedBuyIn: string;
  formattedPrizePool: string;
  formattedWinnerAward: string;
  shortPrizePool: string;
}

export interface TournamentExtended {
  fallbackImageUrl: string;
  formattedGame: string;
  formattedName: string;
  formattedSidecarName: string;
  formattedState: TournamentState;
  imageUrl: string;
  isPlayMoney: boolean;
  nextWhenStartInt?: number;
  resumeTimeInt?: number;
  tournNumDays?: number;
  tags: PokerGameTileTag[];
}

export type Tournament = Omit<TournamentRaw, '__typename'> &
  TournamentAttributes &
  TournamentExtended & { __typename: TournamentRawTypeName | 'Update' };

export type TournamentsData = Record<string, Tournament>;

export type TournamentsCurrency = TournamentRaw['currency'] | 'MULTIPLE';

export interface NormalizedTournamentsResponse {
  tournamentsData: TournamentsData;
  tournamentsCount: number;
  tournamentsCurrency: TournamentsCurrency;
}

interface ColumnProps {
  align?: 'left' | 'right' | 'center';
  display?: 'inline';
  cellStyles?: string;
  lateRegStyle?: string;
  renderValueAccessor?: string;
  cellExpand?: boolean;
}

type Column<D extends object> = ReactTableColumn<D> & UseTableColumnOptions<D> & ColumnProps;

type ColumnInstance<D extends object> = ReactColumnInstance<D> & ColumnProps;

export type TableSortRule = SortingRule<Tournament>;

export type TableFilters = Filters<Tournament>;

export type TableColumn = Column<Tournament>;

export type TableColumnInstance = ColumnInstance<Tournament>;

export enum FilterName {
  Games = 'games',
  BuyIn = 'buy-in',
  TableSize = 'table-size',
  Speed = 'speed'
}

export type FilterNameType = (typeof FilterName)[keyof typeof FilterName];

export enum FilterSectionNameGame {
  Holdem = 'holdem',
  Omaha = 'omaha',
  Stud = 'stud',
  Draw = 'draw',
  Mixed = 'mixed'
}

export interface FilterOptionBase {
  id: string;
  label: TrackedTranslation;
  value: string;
}

export type FilterOptionGroup = Omit<FilterOptionBase, 'value'> & {
  options: FilterOptionBase[];
};

export type FilterOption = FilterOptionBase | FilterOptionGroup;

export interface Filter {
  name: FilterNameType;
  title: TrackedTranslation;
  hasGroups: boolean;
  options: FilterOption[];
}

export interface FiltersDataConfig {
  RM: Filter[];
  PM: Filter[];
}

export type PlayMode = ReturnType<typeof select.currency.getPlayMode>;
export type RawOption = string;

export interface RawSection {
  name: string;
  options: RawOption[];
}

export interface RawFilter {
  name: string;
  sections?: RawSection[];
  options?: RawOption[];
}

export interface FiltersData {
  main: RawFilter[];
  more: RawFilter[];
}

export interface FilterTypes {
  cash: FiltersData;
  events: FiltersData;
  sng: FiltersData;
  tourney: FiltersData;
  zoom: FiltersData;
}

export interface FiltersConfig {
  criteria: string;
  pm: FilterTypes;
  rm: FilterTypes;
}

export interface FilterVariables {
  channels?: Channel[];
  games?: Game[];
  seats?: number[];
  speeds?: Speed[];
  state?: TournamentState[];
  structures?: Structure[];
  total?: Range;
  upcomingOnly?: boolean;
}

export enum FilterOptionGame {
  // Holdem
  HoldemNL = 'holdem.nl',
  HoldemPL = 'holdem.pl',
  HoldemFL = 'holdem.fl',
  HoldemShowTime = 'showtime-holdem',
  HoldemSixPlus = 'sixplus-holdem',
  HoldemSplit = 'split-holdem',
  HoldemSwap = 'swap-holdem',
  HoldemTempest = 'tempest-holdem',
  HoldemDeepWater = 'deepwater-holdem',
  // Omaha
  OmahaPL = 'omaha.pl',
  OmahaNL = 'omaha.nl',
  Omaha5 = 'omaha5',
  Omaha6 = 'omaha6',
  OmahaHiLo = 'omaha.hl',
  OmahaShowTime = 'showtime-omaha',
  OmahaSplit = 'split-omaha',
  OmahaCourchevel = 'courchevel',
  OmahaFusion = 'fusion',
  // Mixed
  Mixed8Game = '8game',
  MixedHoldem = 'holdem.mixed',
  MixedHoldemNLOmahaPL = 'holdem.nl.omaha.pl',
  MixedHorse = 'horse',
  MixedHose = 'hose',
  MixedOmaha = 'omaha.mixed',
  // Stud
  Stud = 'stud',
  Razz = 'razz',
  // Draw
  Draw = 'draw',
  TripleDraw27 = '3draw27',
  SingleDraw27 = 'draw27',
  Badugi = 'badugi'
}

export enum FilterOptionBuyIn {
  Freeroll = 'freeroll',
  Micro = 'micro',
  Low = 'low',
  Medium = 'medium',
  High = 'high',
  Custom = 'custom',
  // Not supported for now
  Starscoin = 'starscoin'
}

export enum FilterOptionTableSize {
  Normal = 'normal',
  Short = 'short',
  HeadsUp = 'heads-up'
}

export enum FilterOptionSpeed {
  Slow = 'slow',
  Normal = 'normal',
  Turbo = 'turbo',
  Hyper = 'hyper'
}

export type CustomRangeBuyIn = number[];

export type CustomRangePayload = object | CustomRangeBuyIn;

export interface FilterOptionLabelMap {
  [FilterName.Games]: Partial<{
    [K in FilterOptionGame]: TranslationTag;
  }>;
  [FilterName.BuyIn]: Partial<{
    [K in FilterOptionBuyIn]: TranslationTag;
  }>;
  [FilterName.TableSize]: {
    [K in FilterOptionTableSize]: TranslationTag;
  };
  [FilterName.Speed]: {
    [K in FilterOptionSpeed]: TranslationTag;
  };
}

export type FilterOptionVariableMap = {
  [FilterName.Games]: {
    [K in FilterOptionGame]: Game;
  };
  [FilterName.BuyIn]: Partial<{
    [K in FilterOptionBuyIn]: {
      RM: { maximum: number; minimum: number };
      PM: { maximum: number; minimum: number };
    };
  }>;
  [FilterName.TableSize]: {
    [K in FilterOptionTableSize]: number[];
  };
  [FilterName.Speed]: { [K in FilterOptionSpeed]: Speed };
} & { structure: Partial<{ [K in FilterOptionGame]: Structure }> };

export interface FilterOptionGroupLabelMap {
  [FilterName.Games]: Partial<{
    [K in FilterSectionNameGame]: TranslationTag;
  }>;
}

export type ActiveUiFilters = {
  [K in FilterName]?: FilterOptionBase[];
};
